import Breadcrumb from "components/Common/Breadcrumb"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Badge, Button, Card, CardBody, Col, Container, Label, Row, Spinner, Table } from "reactstrap"
import { DateRangePicker } from "rsuite"
import { getPredefinedDateRanges } from "helpers/dateRanges"
import { getRenewalList, getRenewalStats, sendAllRenewalReminders, sendRenewalReminder } from "helpers/backendHelpers/renewalReminders"
import { exportStyledXlsx } from "helpers/exportExcel"
import RenewalPaymentQrModal from "components/RenewalPaymentQrModal"

const todayRange = () => {
  const today = new Date()
  return [today, today]
}

const toLocalDate = value => {
  const date = new Date(value)
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

const formatDate = value => value ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)) : "-"

const formatMoney = value => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`

const formatSigned = value => `${Number(value) < 0 ? "-" : "+"}${formatMoney(Math.abs(value))}`

// `lines` is an optional breakdown: [{ label, value, color }] shown under the main amount.
// A period (Past / Next 30 days) groups its cards under one heading so it is clear which dates they cover.
const StatGroup = ({ title, subtitle, accent, xl, children }) => (
  <Col xl={xl} className="mb-3">
    <div className={`rounded p-3 h-100 bg-light border-top border-3 border-${accent}`}>
      <h6 className="mb-0">{title}</h6>
      <div className="text-muted small mb-3">{subtitle}</div>
      <Row>{children}</Row>
    </div>
  </Col>
)

const StatCard = ({ title, range, value, hint, color, lines = [], md = 4 }) => (
  <Col md={md} className="mb-2 mb-md-0">
    <div className="border rounded p-3 h-100 bg-white">
      <div className="fw-medium">{title}</div>
      <div className="text-muted small mb-2">{range}</div>
      <h4 className={`mb-1 ${color || ""}`}>{value}</h4>
      <div className="small text-muted">{hint}</div>
      {lines.length > 0 && (
        <div className="border-top mt-2 pt-2 small">
          {lines.map(line => (
            <div key={line.label} className="d-flex justify-content-between gap-2">
              <span className="text-muted">{line.label}</span>
              <strong className={line.color || ""}>{line.value}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  </Col>
)

const renderSendStatus = (count, status, error) => {
  if (Number(count || 0)) return <Badge color="success">{count} Sent</Badge>
  if (error === "not_on_whatsapp") return <Badge color="warning" title="This mobile number is not registered on WhatsApp">Not on WhatsApp</Badge>
  if (status === "failed") return <Badge color="danger" title={error || "Message could not be sent"}>Failed</Badge>
  return <Badge color="secondary">Not sent</Badge>
}

const Index = () => {
  const [dateRange, setDateRange] = useState(todayRange)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [sendingUserId, setSendingUserId] = useState(null)
  const [sendingAll, setSendingAll] = useState(false)
  const [paymentQrRow, setPaymentQrRow] = useState(null)
  const [stats, setStats] = useState(null)

  const startDate = dateRange?.[0] ? toLocalDate(dateRange[0]) : ""
  const endDate = dateRange?.[1] ? toLocalDate(dateRange[1]) : ""
  const hasDateRange = Boolean(startDate && endDate)

  const loadList = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getRenewalList({ startDate, endDate })
      setRows(response?.payload?.data || [])
    } catch (_) {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    loadList()
  }, [loadList])

  useEffect(() => {
    getRenewalStats().then(response => setStats(response?.payload || null)).catch(() => setStats(null))
  }, [])

  const sendOne = async userId => {
    setSendingUserId(userId)
    try {
      await sendRenewalReminder({ userId, startDate, endDate })
      await loadList()
    } catch (_) {
      // Shared API helper shows the error.
    } finally {
      setSendingUserId(null)
    }
  }

  const sendAll = async () => {
    const sendableCount = rows.filter(row => row.mobileNo).length
    if (!sendableCount || !window.confirm(`Send renewal reminder to ${sendableCount} listed user(s)?`)) return
    setSendingAll(true)
    try {
      await sendAllRenewalReminders({ startDate, endDate })
      await loadList()
    } catch (_) {
      // Shared API helper shows the error.
    } finally {
      setSendingAll(false)
    }
  }

  const sendableCount = useMemo(() => rows.filter(row => row.mobileNo).length, [rows])
  const title = hasDateRange ? "Expiring in Selected Date Range" : "All Eligible Paid Renewals"
  const exportRows = useMemo(() => rows.map(row => ({
    "User": row.customerName,
    "Mobile Number": row.mobileNo || "",
    "Plan": row.planName,
    "Amount": Number(row.amount || 0),
    "Expiry Date": formatDate(row.expiryDate),
    "Picked By": row.claimedByName || "",
    "Reminder Status": Number(row.sendCount || 0) ? `${row.sendCount} Sent` : row.reminderStatus === "failed" ? "Failed" : "Not sent",
    "Reminder Count": Number(row.sendCount || 0),
    "Last Sent": formatDate(row.sentAt),
    "QR Sent": Number(row.qrSendCount || 0) ? `${row.qrSendCount} Sent` : row.qrSendStatus === "failed" ? "Failed" : "Not sent",
    "QR Send Count": Number(row.qrSendCount || 0),
    "QR Last Sent": formatDate(row.qrSentAt),
  })), [rows])

  const downloadExcel = () => {
    const fileRange = hasDateRange ? `${startDate}-to-${endDate}` : "all-time"
    exportStyledXlsx({ rows: exportRows, fileName: `Renewal-List-${fileRange}` })
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Renew List" breadcrumbItem="Renewal WhatsApp Reminders" />
        <RenewalPaymentQrModal
          isOpen={Boolean(paymentQrRow)}
          toggle={() => setPaymentQrRow(null)}
          row={paymentQrRow}
          startDate={startDate}
          endDate={endDate}
          onSent={loadList}
        />
        {stats && (
          <Card>
            <CardBody>
              <h5 className="mb-1">Renewal Overview <span className="text-muted fs-6">(excluding GST)</span></h5>
              <p className="text-muted small mb-3">
                Target = plan prices of everything that expired. Collected = what renewed users were actually charged, so it
                is higher than their old plan prices when customers upgrade.
              </p>
              <Row>
                <StatGroup
                  xl={9}
                  accent="secondary"
                  title="Past 30 Days"
                  subtitle={`${formatDate(stats.past.from)} - ${formatDate(stats.past.to)} · plans that expired in this period`}
                >
                  <StatCard
                    title="Target"
                    range="Expired plans"
                    value={formatMoney(stats.past.targetAmount)}
                    hint={`${stats.past.targetCount} plan(s) expired · total of their plan prices`}
                  />
                  <StatCard
                    title="Renewed (Collected)"
                    range={`${stats.past.targetAmount ? Math.round(stats.past.renewedTargetAmount / stats.past.targetAmount * 100) : 0}% of target renewed`}
                    value={formatMoney(stats.past.achievedAmount)}
                    hint={`${stats.past.achievedCount} user(s) renewed`}
                    color="text-success"
                    lines={[
                      { label: "Their old plan prices", value: formatMoney(stats.past.renewedTargetAmount) },
                      {
                        label: "Upgrade / downgrade (net)",
                        value: formatSigned(stats.past.achievedAmount - stats.past.renewedTargetAmount),
                        color: stats.past.achievedAmount >= stats.past.renewedTargetAmount ? "text-success" : "text-danger",
                      },
                    ]}
                  />
                  <StatCard
                    title="Still Pending"
                    range="Not renewed yet"
                    value={formatMoney(stats.past.pendingAmount)}
                    hint={`${stats.past.pendingCount} user(s) not renewed yet`}
                    color="text-danger"
                  />
                </StatGroup>
                <StatGroup
                  xl={3}
                  accent="primary"
                  title="Next 30 Days"
                  subtitle={`${formatDate(stats.upcoming.from)} - ${formatDate(stats.upcoming.to)} · plans expiring in this period`}
                >
                  <StatCard
                    md={12}
                    title="Forecast"
                    range="Expected renewals"
                    value={formatMoney(stats.upcoming.forecastAmount)}
                    hint={`${stats.upcoming.userCount} user(s) due for renewal`}
                    color="text-primary"
                  />
                </StatGroup>
              </Row>
            </CardBody>
          </Card>
        )}
        <Card>
          <CardBody>
            <div className="d-flex flex-column flex-lg-row align-items-lg-end justify-content-between gap-3 mb-4">
              <div>
                <h5 className="mb-1">Renewal List</h5>
                <p className="text-muted mb-0">Only users with a latest successful paid base-plan subscription are shown.</p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button color="primary" disabled={loading || !exportRows.length} onClick={downloadExcel}>
                  <i className="bx bx-download me-1" />Download Excel
                </Button>
                <Button color="success" disabled={loading || sendingAll || sendableCount === 0} onClick={sendAll}>
                  {sendingAll ? <><Spinner size="sm" className="me-1" />Sending...</> : <><i className="bx bxl-whatsapp me-1" />Send All ({sendableCount})</>}
                </Button>
              </div>
            </div>
            <Row className="align-items-end mb-3">
              <Col md={6} lg={4}>
                <Label>Expiry Date Range</Label>
                <DateRangePicker
                  ranges={getPredefinedDateRanges()}
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder="Expiry Date"
                  format="dd/MM/yyyy"
                  showOneCalendar
                  cleanable={false}
                  block
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">{title}</h6>
              <Badge color="secondary" pill>{rows.length} user(s)</Badge>
            </div>
            <div className="table-responsive">
              <Table className="table-nowrap align-middle mb-0">
                <thead className="table-light">
                  <tr><th>User</th><th>Mobile Number</th><th>Plan</th><th>Amount</th><th>Expiry Date</th><th>Picked By</th><th>Reminder</th><th>QR Sent</th><th className="text-end">Action</th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="9" className="text-center py-5"><Spinner color="primary" /></td></tr>
                  ) : rows.length ? rows.map(row => {
                    const sentCount = Number(row.sendCount || 0)
                    const qrSentCount = Number(row.qrSendCount || 0)
                    const cannotSend = !row.mobileNo
                    const shouldRetryReminder = row.reminderStatus === "failed" || row.lastError === "not_on_whatsapp"
                    const shouldRetryQr = row.qrSendStatus === "failed" || row.qrLastError === "not_on_whatsapp"
                    return (
                      <tr key={row.userId}>
                        <td className="fw-medium">{row.customerName}</td>
                        <td>{row.mobileNo || <span className="text-danger">No mobile number</span>}</td>
                        <td>{row.planName}</td>
                        <td>₹{row.amount}</td>
                        <td>{formatDate(row.expiryDate)}</td>
                        <td>{row.claimedByName || <span className="text-muted">-</span>}</td>
                        <td>
                          {renderSendStatus(sentCount, row.reminderStatus, row.lastError)}
                        </td>
                        <td>
                          {renderSendStatus(qrSentCount, row.qrSendStatus, row.qrLastError)}
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex align-items-center gap-2">
                            {sentCount > 0 && <span className="text-success small">Last sent {formatDate(row.sentAt)}</span>}
                            <Button color={shouldRetryReminder ? "warning" : "success"} size="sm" disabled={cannotSend || sendingUserId === row.userId || sendingAll} onClick={() => sendOne(row.userId)}>
                              {sendingUserId === row.userId ? <Spinner size="sm" /> : shouldRetryReminder ? "Retry Send WhatsApp" : "Send WhatsApp"}
                            </Button>
                            <Button color={shouldRetryQr ? "warning" : "primary"} outline={!shouldRetryQr} size="sm" disabled={cannotSend || sendingAll} onClick={() => setPaymentQrRow(row)}>
                              {shouldRetryQr ? "Retry Generate QR Code" : "Generate QR Code"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  }) : (
                    <tr><td colSpan="9" className="text-center text-muted py-5">No eligible paid users found for this selection.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default Index
